import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Chip,
  TablePagination,
  Tooltip,
} from '@mui/material';
import { IoSearch } from 'react-icons/io5';
import { MdModeEdit, MdAdd, MdShield, MdDeleteOutline } from 'react-icons/md';
import { useSnackbar } from 'notistack';
import { getRolesDetails, deleteRole } from "../api's/ManagementApi";
import EditRoleDialog from '../components/Admin/EditRoleDialog';
import ConfirmDeleteDialog from '../components/Admin/ConfirmDeleteDialog';

function RolesPermissionsPage() {
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    filterRoles();
  }, [roles, searchTerm]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await getRolesDetails();
      setRoles(response.data);
      setFilteredRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      enqueueSnackbar('Failed to fetch roles', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filterRoles = () => {
    if (searchTerm) {
      const filtered = roles.filter(
        (role) =>
          role.roleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          role.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRoles(filtered);
    } else {
      setFilteredRoles(roles);
    }
  };

  const getRoleColorScheme = (roleName) => {
    const schemes = {
      ADMIN: { bg: '#E3F2FD', color: '#1565C0' },
      SUPER_USER: { bg: '#F3E5F5', color: '#6A1B9A' },
      READER: { bg: '#F5F5F5', color: '#616161' },
      EXECUTOR: { bg: '#E8F5E9', color: '#2E7D32' },
    };
    return schemes[roleName] || { bg: '#FFF3E0', color: '#E65100' };
  };

  const getGroupCountColor = (count) => {
    if (count === 0) return { bg: '#FFF3E0', color: '#EF6C00', border: '1px solid #FFB74D' };
    return { bg: '#E3F2FD', color: '#1565C0', border: '1px solid #90CAF9' };
  };

  const handleAddRole = () => {
    setSelectedRole(null);
    setEditDialogOpen(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setEditDialogOpen(true);
  };

  const handleDeleteRoleClick = (role) => {
    setRoleToDelete(role);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (!deleting) {
      setDeleteConfirmOpen(false);
      setRoleToDelete(null);
    }
  };

  const handleConfirmDeleteRole = async () => {
    if (!roleToDelete) return;
    setDeleting(true);
    try {
      await deleteRole(roleToDelete.roleId);
      enqueueSnackbar('Role deleted successfully', { variant: 'success' });
      setDeleteConfirmOpen(false);
      setRoleToDelete(null);
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Failed to delete role', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveRole = () => {
    fetchRoles();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRoles = filteredRoles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ padding: '0px 16px' }}>
      {/* Header - matching Users page */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Roles & Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure role-based access control across the platform.
          </Typography>
        </Box>
        <Button
            type="submit"
            variant="contained"
            sx={{ backgroundColor: '#00043C', '&:hover': { backgroundColor: '#00043C' } }}
            onClick={handleAddRole}
          >
            <MdAdd />
            Add New Role
          </Button>
      </Box>

      {/* Search Bar - matching Users page */}
      <Paper sx={{ mb: 2, p: 1.5, borderRadius: 1 }}>
        <TextField
          placeholder="Search by role name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IoSearch />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Modern Table Container */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid #e8e8e8',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 6 }}>
            <CircularProgress size={40} thickness={3.5} sx={{ color: '#00043C' }} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#fafafa',
                  borderBottom: '2px solid #e8e8e8',
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    color: '#999',
                    letterSpacing: '0.8px',
                    py: 1.5,
                  }}
                >
                  Role
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    color: '#999',
                    letterSpacing: '0.8px',
                    py: 1.5,
                  }}
                >
                  Description
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    color: '#999',
                    letterSpacing: '0.8px',
                    py: 1.5,
                  }}
                >
                  Groups
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    color: '#999',
                    letterSpacing: '0.8px',
                    py: 1.5,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRoles.map((role, index) => {
                const roleColors = getRoleColorScheme(role.roleName);
                const groupCountStyle = getGroupCountColor(role.groupCount);
                return (
                  <TableRow
                    key={role.roleId}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f0f2ff',
                        '& .edit-role-btn': {
                          backgroundColor: '#fff',
                          borderColor: '#d0d5f0',
                          boxShadow: '0 1px 2px rgba(0, 4, 60, 0.08)',
                        },
                        '& .edit-role-btn:hover': {
                          backgroundColor: '#00043C',
                          color: '#fff',
                          borderColor: '#00043C',
                          transform: 'scale(1.05)',
                          boxShadow: '0 2px 8px rgba(0, 4, 60, 0.35)',
                        },
                        transition: 'background-color 0.2s ease',
                      },
                      borderBottom: index === paginatedRoles.length - 1 ? 'none' : '1px solid #f0f0f0',
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 700, 
                            fontSize: '14px',
                            color: '#1a1a1a',
                          }}
                        >
                          {role.roleName}
                        </Typography> */}
                        <Chip
                          label={role.roleName}
                          size="small"
                          sx={{
                            backgroundColor: roleColors.bg,
                            color: roleColors.color,
                            fontWeight: 600,
                            fontSize: '11px',
                            height: '22px',
                            textTransform: 'uppercase',
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, maxWidth: '400px' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '13px',
                          color: '#666',
                          lineHeight: 1.4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {role.description || 'No description provided'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${role.groupCount}`}
                          size="small"
                          sx={{
                            backgroundColor: groupCountStyle.bg,
                            color: groupCountStyle.color,
                            border: groupCountStyle.border,
                            fontWeight: 700,
                            fontSize: '12px',
                            minWidth: '32px',
                            height: '24px',
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#999',
                            fontSize: '11px',
                          }}
                        >
                          {role.groupCount === 1 ? 'group' : 'groups'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Tooltip title="Edit role" arrow placement="top">
                          <span>
                            <IconButton
                              className="edit-role-btn"
                              size="small"
                              onClick={() => handleEditRole(role)}
                              sx={{
                                color: '#00043C',
                                backgroundColor: '#f0f2ff',
                                width: '30px',
                                height: '30px',
                                border: '1px solid #e8eaff',
                                borderRadius: '7px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#00043C',
                                  color: '#fff',
                                  borderColor: '#00043C',
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 2px 8px rgba(0, 4, 60, 0.35)',
                                },
                              }}
                            >
                              <MdModeEdit size={15} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete role" arrow placement="top">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRoleClick(role)}
                              sx={{
                                color: '#d32f2f',
                                backgroundColor: '#ffebee',
                                width: '30px',
                                height: '30px',
                                border: '1px solid #ffcdd2',
                                borderRadius: '7px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#ffcdd2',
                                  borderColor: '#d32f2f',
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 2px 8px rgba(211, 47, 47, 0.25)',
                                },
                              }}
                            >
                              <MdDeleteOutline size={15} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {!loading && filteredRoles.length > 0 && (
          <TablePagination
            component="div"
            count={filteredRoles.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: '1px solid #f0f0f0',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: '13px',
                color: '#666',
              },
              '.MuiTablePagination-select': {
                fontSize: '13px',
              },
            }}
          />
        )}
      </TableContainer>

      {/* Modern Empty State */}
      {!loading && filteredRoles.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            textAlign: 'center',
            py: 6,
            borderRadius: '12px',
            border: '1px solid #e8e8e8',
            backgroundColor: '#fafafa',
          }}
        >
          <Box
            sx={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#f0f2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 1.5,
            }}
          >
            <MdShield size={32} color="#00043C" />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#1a1a1a',
              mb: 0.5,
            }}
          >
            No roles found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '13px',
              color: '#666',
              mb: 2,
            }}
          >
            {searchTerm ? 'Try adjusting your search' : 'Get started by creating your first role'}
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              startIcon={<MdAdd size={16} />}
              onClick={handleAddRole}
              sx={{
                backgroundColor: '#00043C',
                color: 'white',
                textTransform: 'none',
                borderRadius: '8px',
                padding: '7px 18px',
                fontSize: '13px',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(0, 4, 60, 0.25)',
                '&:hover': {
                  backgroundColor: '#000652',
                  boxShadow: '0 4px 12px rgba(0, 4, 60, 0.35)',
                },
              }}
            >
              Create Role
            </Button>
          )}
        </Paper>
      )}

      <EditRoleDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        role={selectedRole}
        onSave={handleSaveRole}
      />

      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDeleteRole}
        title="Delete role"
        message={
          roleToDelete
            ? `Are you sure you want to delete the role "${roleToDelete.roleName}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        loading={deleting}
      />
    </Box>
  );
}

export default RolesPermissionsPage;
